import { API_URL } from '@/config/env';
import { conformanceApi } from '@/lib/api';

import type {
  NewsletterPage,
  NewsletterUpsertRequest,
} from './newsletter.types';

const getConformanceSwaggerUiJsonUrl = () => {
  const normalizedApiUrl = API_URL.replace(/\/+$/, '');
  const versionMatch = normalizedApiUrl.match(/\/api\/(v[^/]+)/i);
  const swaggerVersion = versionMatch?.[1] ?? 'v4.0';

  try {
    const parsed = new URL(normalizedApiUrl);
    return `${parsed.origin}/swagger/${swaggerVersion}/swagger.json`;
  } catch {
    const root = normalizedApiUrl.replace(/\/api\/v[^/]+\/orr$/i, '');
    return `${root}/swagger/${swaggerVersion}/swagger.json`;
  }
};

const getSwaggerPaths = (payload: unknown): string[] => {
  if (!payload) return [];

  if (typeof payload === 'string') {
    try {
      return getSwaggerPaths(JSON.parse(payload));
    } catch {
      return [];
    }
  }

  if (typeof payload !== 'object') return [];

  const paths = (payload as { paths?: unknown }).paths;
  if (!paths || typeof paths !== 'object') return [];

  return Object.keys(paths as Record<string, unknown>);
};

export const isNewsletterEnabledBySwagger = async () => {
  try {
    const url = getConformanceSwaggerUiJsonUrl();
    const response = await conformanceApi.get<unknown>(url, {
      headers: {
        accept: 'application/json',
      },
    });
    const paths = getSwaggerPaths(response.data);

    return paths.some((path) => path.toLowerCase().includes('release-notes'));
  } catch {
    return false;
  }
};

export const fetchNewsletterAdmin = () => {
  return conformanceApi.get<NewsletterPage[]>(`${API_URL}/release-notes`);
};

export const fetchNewsletterPublic = () => {
  return conformanceApi.get<NewsletterPage[]>(
    `${API_URL}/release-notes/public`,
  );
};

export const createNewsletter = (data: NewsletterUpsertRequest) => {
  return conformanceApi.post<NewsletterPage>(`${API_URL}/release-notes`, data);
};

export const updateNewsletter = (
  id: string,
  data: NewsletterUpsertRequest,
) => {
  return conformanceApi.put<NewsletterPage>(
    `${API_URL}/release-notes/${id}`,
    data,
  );
};

export const deleteNewsletter = (id: string) => {
  return conformanceApi.delete<void>(`${API_URL}/release-notes/${id}`);
};
